import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { type ScheduledCrossClientQuery } from '../../../../models/ScheduledCrossClientQuery'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientLandingTeammates } from '../../../../store/crossClient/crossClientLanding'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { formatFriendlyDateTime } from '../../../../common/dateUtils'
import { CrossClientResultSourceIcon } from '../../Results/Sources/CrossClientResultSourceIcon'
import { sortSources } from '../../Results/Sources/utils'
import { onActivateKeyDown } from '../../utils'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import {
  MONTH_LABELS,
  WEEKDAY_LABELS,
  cronExpressionsToFrequency,
  FrequencyUnits,
  getNextRunForCron,
  ordinal,
  type ScheduledQueryFrequency,
} from '../../Scheduled/scheduledQueryFrequency'
import './CrossClientLandingScheduled.scss'

const cadenceLabel = (frequency: ScheduledQueryFrequency, timeLabel: string): string => {
  const at = timeLabel ? ` at ${timeLabel}` : ''
  switch (frequency.unit) {
    case FrequencyUnits.MONTH:
      return `Run on the ${ordinal(frequency.dayOfMonth)} of every month${at}`
    case FrequencyUnits.YEAR:
      return `Run every ${MONTH_LABELS[frequency.month]} ${ordinal(frequency.dayOfMonth)}${at}`
    case FrequencyUnits.WEEK:
    default:
      return `Run every ${WEEKDAY_LABELS[frequency.dayOfWeek]}${at}`
  }
}

interface CrossClientScheduledElementProps {
  query: ScheduledCrossClientQuery
}

export const CrossClientScheduledElement: React.FC<CrossClientScheduledElementProps> = ({ query }) => {
  const navigate = useNavigate()
  const teammates = useAppSelector(selectCrossClientLandingTeammates)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const [hovered, setHovered] = useState<boolean>(false)

  const resolveName = (userId: string): string =>
    getUserDisplayName(teammates.find((teammate) => teammate.id === userId)) || (userId === currentUser.id ? getUserDisplayName(currentUser) : '')

  const frequency = cronExpressionsToFrequency(query.schedule.cronExpressions)
  const nextRun = getNextRunForCron(query.schedule.cronExpressions)
  const timeLabel = nextRun ? nextRun.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''
  const ownerName = query.managedBy?.id ? resolveName(query.managedBy.id) : ''
  const openEdit = () => navigate(`/cross-client/scheduled-queries/${query.id}/edit`, { state: { fromTab: 'scheduled' } })
  const sources = sortSources(query.definition.sources ?? [])

  return (
    <div
      className={classNames(
        'd-flex zocks-gap-2 justify-content-between align-items-center p-3 border border-1 zocks-border-0.75 bg-white w-100 cross-client-scheduled-element cross-client-transition',
        {
          'zocks-border-color-primary cross-client-history-element-box-shadow': hovered,
          'zocks-border-color-gray-300': !hovered,
        },
      )}
      role='button'
      tabIndex={0}
      onClick={openEdit}
      onKeyDown={onActivateKeyDown(openEdit)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='d-flex flex-column gap-2 flex-grow-1 mw-0'>
        <div className='paragraph-medium medium text-truncate'>{query.name || query.definition.userQuestion}</div>
        <div className='d-flex gap-2 align-items-center paragraph-small zocks-text-gray-700'>
          <div className='d-flex gap-1 align-items-center'>
            <ZocksIcon width='0.875rem' height='0.875rem' />
            {sources.map((source, idx) => (
              <CrossClientResultSourceIcon key={idx} source={source} dimension='0.875rem' />
            ))}
          </div>
          <div className='cross-client-scheduled-meta-separator' />
          <div className='d-flex gap-1 align-items-center text-truncate'>
            <i className='bi bi-calendar' />
            <span className='text-truncate'>
              {cadenceLabel(frequency, timeLabel)}
              {ownerName ? ` for ${ownerName}` : ''}
            </span>
          </div>
        </div>
      </div>
      {nextRun ? (
        <div
          className={classNames(
            'd-flex gap-1 align-items-center px-2 py-1 border zocks-border-0.5 zocks-border-color-gray-300 bg-white cross-client-scheduled-next-run',
            {
              'opacity-50': !query.active,
            },
          )}
        >
          <i className='bi bi-calendar zocks-text-gray-700' />
          <span className='paragraph-small bold zocks-text-gray-900'>{formatFriendlyDateTime(nextRun)}</span>
        </div>
      ) : null}
    </div>
  )
}
